import { onAnimationEnd } from '@theme/utilities';
import { Component } from '@theme/component';

/**
 * @typedef {object} AccordionRefs
 * @property {DetailsCustom[]} accordionRows
 * @extends Component<AccordionRefs>
 */
class DetailsAccordion extends Component {
  #abortController = new AbortController();

  get #allowMultiple() {
    return this.hasAttribute('allow-multiple');
  }

  connectedCallback() {
    super.connectedCallback();

    const signal = this.#abortController.signal;

    this.addEventListener(
      'open',
      (e) => {
        if (!this.#allowMultiple) {
          this.refs.accordionRows.forEach((row) => {
            if (row !== e.target) {
              row.toggle(false);
            }
          });
        }
      },
      { signal },
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }
}

if (!customElements.get('details-accordion')) {
  customElements.define('details-accordion', DetailsAccordion);
}

/**
 * @typedef {object} DetailsAccordionRowRefs
 * @property {HTMLElement} accordionSummary
 * @property {HTMLElement} accordionContentWrapper
 * @property {HTMLElement} accordionContent
 * @property {HTMLElement} accordionShowMoreButton
 * @extends Component<DetailsAccordionRowRefs>
 */
class DetailsCustom extends Component {
  requiredRefs = ['accordionSummary', 'accordionContentWrapper', 'accordionContent'];

  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    const { accordionSummary: summary, accordionShowMoreButton: showMoreButton } = this.refs;

    summary.addEventListener('click', () => this.toggle(), { signal });
    showMoreButton?.addEventListener('click', () => this.toggle(), { signal });
  }

  get #open() {
    return this.hasAttribute('open');
  }

  /**
   * @param {boolean} open
   */
  async toggle(open = !this.#open) {
    if (this.#open !== open) {
      if (open) this.dispatchEvent(new CustomEvent('open', { bubbles: true }));

      const {
        accordionSummary: summary,
        accordionContentWrapper: contentWrapper,
        accordionContent: content,
        accordionShowMoreButton: showMoreButton,
      } = this.refs;

      this.toggleAttribute('open', open);

      summary.setAttribute('aria-expanded', open ? 'true' : 'false');
      showMoreButton?.setAttribute('aria-expanded', open ? 'true' : 'false');
      contentWrapper.toggleAttribute('inert', !open);

      // Reset to auto height after animation
      onAnimationEnd(contentWrapper, () => {
        contentWrapper.style.maxHeight = this.#open ? `` : 'var(--closed-max-height)';
      });

      if (!open) {
        // Animate from px value
        contentWrapper.style.maxHeight = `${content.clientHeight}px`;
        await new Promise((resolve) => setTimeout(resolve));
      }

      // Animate to px value
      contentWrapper.style.maxHeight = this.#open ? `${content.clientHeight}px` : 'var(--closed-max-height)';
    }
  }
}

if (!customElements.get('details-custom')) {
  customElements.define('details-custom', DetailsCustom);
}
